import React, { Fragment, useEffect, useState } from 'react';
import Helmet from 'react-helmet';
import { Link, useRouteMatch } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Typography from '@material-ui/core/Typography';
import LinearProgress from '@material-ui/core/LinearProgress';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';

import Img from 'retired/Img';
import Box from 'retired/Box';
import { ViewInAppButton } from 'components/ViewInAppButton';
import { selectUpcomingDeedsForUser, selectPastDeedsForUser } from 'entities/deed/selectors';
import CapBalance from 'retired/CapBalance';
import { selectDonationsForUser } from 'entities/donation/selectors';
import { selectVolunteerById } from 'entities/volunteers/selectors';
import { selectVolunteerTimesByUser } from 'entities/volunteerTime/selectors';
import { selectCreditingTransactionsByUser } from 'entities/walletTransaction/selectors';
import { selectOrganization, selectUserLocations, selectUser } from 'entities/user/selectors';
import { selectDonationSchedulesByUser } from 'entities/donationSchedule/selectors';
import { selectSkills } from 'entities/skill/selectors';
import { selectVolunteerTimeOffByUser } from 'entities/volunteerTimeOff/selector';
import { useInjectReducer } from 'utils/injectReducer';
import { useInjectEpics } from 'utils/injectEpics';
import { currenciesFormat } from 'utils/currencyFormat';
import { Features } from 'utils/features';
import { Privileges } from 'utils/privileges';
import { convertVtoDuration } from 'utils/convertDuration';
import { getAppDomain } from 'utils/getAppDomain';
import AffiliationsList from 'containers/AffiliationsList/index';
import { formatFullName } from 'utils/formatFullName';
import Tooltip from 'retired/Tooltip';
import type { State } from 'reducers';

import * as Actions from './actions';
import { selectTab, selectLoading, selectImpersonating } from './selectors';
import epics from './epics';
import reducer from './reducer';
import VolunteerHoursAndTimeOff from './VolunteerHoursAndTimeOff';
import Deed from './Deed';
import Donation from './Donation';
import WalletTransaction from './WalletTransaction';
import DonationSchedule from './DonationSchedule';
import ImpersonationModal from './ImpersonationModal';

const key = 'volunteerPage';

const VolunteerPage = () => {
  useInjectReducer({ key, reducer });
  useInjectEpics({ key, epics });
  const dispatch = useDispatch();

  const [showImpersonationModal, setShowImpersonationModal] = useState(false);

  const match = useRouteMatch<{ id?: string }>();
  const volunteerId = match.params?.id;

  const volunteer = useSelector((state: State) => selectVolunteerById(state, volunteerId));
  const me = useSelector((state: State) => selectUser(state));
  const organization = useSelector((state: State) => selectOrganization(state));
  const locations = useSelector((state: State) => selectUserLocations(state));
  const loading = useSelector(selectLoading);
  const tab = useSelector(selectTab);
  const impersonating = useSelector(selectImpersonating);
  const skills = useSelector(selectSkills);
  const donations = useSelector((state: State) => selectDonationsForUser(state, volunteerId));
  const upcomingDeeds = useSelector((state: State) =>
    selectUpcomingDeedsForUser(state, volunteerId)
  );
  const pastDeeds = useSelector((state: State) => selectPastDeedsForUser(state, volunteerId));
  const volunteerTimes = useSelector((state: State) =>
    selectVolunteerTimesByUser(state, volunteerId)
  );
  const walletTransactions = useSelector((state: State) =>
    selectCreditingTransactionsByUser(state, volunteerId)
  );
  const volunteerTimesOff = useSelector((state: State) =>
    selectVolunteerTimeOffByUser(state, volunteerId)
  );
  const donationSchedules = useSelector((state: State) =>
    selectDonationSchedulesByUser(state, volunteerId)
  );

  const volunteerIsFromMyCompany =
    volunteer?.organization && volunteer.organization === me?.organization?.id;

  useEffect(() => {
    if (me && volunteerId) {
      dispatch(Actions.initAction(volunteerId));
    }
  }, [me, volunteerId]);

  const userLocation = locations?.get(volunteer?.location);
  const formResponses = volunteer?.get('formResponses');

  if (!volunteer) {
    return <LinearProgress />;
  }

  let volunteerTimeOffSummaryForDisplay;
  if (
    !volunteer.volunteerTimeOffSummary ||
    !Object.keys(volunteer.volunteerTimeOffSummary).length ||
    Object.values(volunteer.volunteerTimeOffSummary).includes(null) ||
    volunteer.volunteerTimeOffSummary.total === 0
  ) {
    volunteerTimeOffSummaryForDisplay = 'N/A';
  } else {
    volunteerTimeOffSummaryForDisplay = `${convertVtoDuration(
      volunteer.volunteerTimeOffSummary.balance
    )} (out of ${convertVtoDuration(volunteer.volunteerTimeOffSummary?.total)})`;
  }

  return (
    <>
      <Box>
        <Helmet title="User Profile" />
        <div style={{ display: 'flex', marginBottom: 20 }}>
          <Img
            src={volunteer.mainPicture}
            alt="Profile picture"
            style={{
              height: 100,
              width: 100,
              display: 'block',
              borderRadius: 100,
            }}
          />

          <div style={{ marginLeft: 20, width: '100%' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <h1 style={{ fontSize: 25, margin: 0 }}>{formatFullName(volunteer.fullName)}</h1>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <ViewInAppButton
                  href={`${getAppDomain([organization, volunteer])}/profile/${volunteer.id}`}
                  style={{
                    display: 'block',
                  }}
                />

                {volunteerIsFromMyCompany && (
                  <>
                    {me?.hasPrivilege(Privileges.ORGANIZATION_IMPERSONATE_USERS) &&
                      (me?.hasFeature(Features.IMPERSONATION) || me?.isAdmin) && (
                        <Button
                          onClick={() => setShowImpersonationModal(true)}
                          style={{ marginLeft: 20 }}
                          disabled={impersonating}
                        >
                          {impersonating ? (
                            <CircularProgress size={24} color="inherit" />
                          ) : (
                            'Impersonate'
                          )}
                        </Button>
                      )}
                    {me?.hasPrivilege(Privileges.ORGANIZATION_MANAGE_USERS) && (
                      <Link
                        style={{
                          display: 'block',
                          marginLeft: 20,
                        }}
                        to={`/volunteers/${volunteer.id}/edit`}
                      >
                        <Button variant="outlined">Edit</Button>
                      </Link>
                    )}
                  </>
                )}
              </div>
            </div>

            {me?.hasPrivilege(Privileges.ORGANIZATION_MANAGE_USERS) && (
              <>
                {volunteer.preferredName ? (
                  <div>
                    <strong>Chosen name:</strong> {formatFullName(volunteer.preferredName)}
                  </div>
                ) : null}
                <div>{volunteer.email || (volunteer.facebook && volunteer.facebook.email)}</div>
                <div>{volunteer.phone}</div>
              </>
            )}
            {volunteerIsFromMyCompany && volunteer.createdAt && (
              <div>{`Joined on ${moment(new Date(volunteer.createdAt)).format(
                'MMM DD, YYYY'
              )}`}</div>
            )}
            {volunteerIsFromMyCompany && volunteer.consent?.disclaimer && (
              <div>
                Disclaimer consented at{' '}
                {moment(new Date(volunteer.consent.disclaimer)).format('MMM DD, YYYY')}
              </div>
            )}
            {me?.organization?.hasSetting('allowExtraContactEmails') &&
              volunteer.contactEmails &&
              volunteer.contactEmails.length > 0 && (
                <div>
                  <strong>Contact emails:</strong>{' '}
                  {volunteer.contactEmails.map((contact) => contact.email).join(', ')}
                </div>
              )}
            {volunteerIsFromMyCompany && me?.hasPrivilege(Privileges.ORGANIZATION_MANAGE_USERS) && (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ marginRight: 10 }}>
                  <strong>Hours:</strong> {Number((volunteer._volunteerMinutes / 60).toFixed(2))}
                </div>
                {me?.hasPrivilege(Privileges.DONATION_VIEW) && (
                  <div style={{ marginRight: 10 }}>
                    <strong>Donations:</strong>{' '}
                    {currenciesFormat(
                      volunteer._donatedAmountCurrencies,
                      volunteer?.displayCurrency
                    )}
                    {donations.size > 0 && ` (${donations.size})`}
                  </div>
                )}
              </div>
            )}
            {volunteerIsFromMyCompany &&
              me?.hasPrivilege(Privileges.ORGANIZATION_MANAGE_USERS) &&
              me?.hasPrivilege(Privileges.VOLUNTEER_TIME_OFF_VIEW) &&
              me?.hasFeature(Features.VOLUNTEER_TIME_OFF) && (
                <div>
                  <strong>VTO Balance:</strong> {volunteerTimeOffSummaryForDisplay}
                </div>
              )}

            {volunteerIsFromMyCompany && volunteer.organizationDepartment && (
              <div>Department: {volunteer.organizationDepartment}</div>
            )}
            {userLocation && <div>Location: {userLocation.name}</div>}
            {volunteer.tShirtSize ? <div>T-Shirt Size: {volunteer.tShirtSize}</div> : null}
            {volunteer && volunteer.skills.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span style={{ marginRight: 10 }}>Skills:</span>
                <div style={{ display: 'flex' }}>
                  {skills.size > 0 &&
                    volunteer.skills.map((skillId) => {
                      const skill = skills.get(skillId);
                      return (
                        <div
                          style={{
                            marginRight: 5,
                            borderRadius: 20,
                            padding: '5px 10px',
                            fontSize: '12px',
                            background: '#EBEEF0',
                            fontFamily: 'Graphik',
                          }}
                          key={skillId}
                        >
                          {(skill && skill.name.replace('-', '')) || skillId}
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
            {volunteerIsFromMyCompany && volunteer.jobTitle && (
              <div>Employee Role: {volunteer.jobTitle}</div>
            )}
            {volunteerIsFromMyCompany &&
              me?.hasFeature(Features.DONATION_MATCHING) &&
              me?.hasPrivilege(Privileges.DONATION_VIEW) && (
                <div>
                  Matching cap balance remaining:{' '}
                  <CapBalance
                    capBalanceByBudget={volunteer._capBalanceByBudget}
                    unlimitedMessaging="unlimited in"
                  />
                </div>
              )}
            {volunteerIsFromMyCompany && volunteer.payrollPayGroup?.payGroupDisplayName && (
              <div style={{ marginRight: 10 }}>
                <strong>Employee Pay Group:</strong>{' '}
                {volunteer.payrollPayGroup?.payGroupDisplayName}
              </div>
            )}
          </div>
        </div>
        <Box square style={{ margin: '15px 0', padding: '0 5px' }}>
          <Tabs
            value={tab}
            onChange={(_event, value) => {
              dispatch(Actions.showTabAction(value));
            }}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab value="upcomingDeeds" label={`Upcoming Deeds (${upcomingDeeds.size})`} />
            <Tab value="completedDeeds" label={`Completed Deeds (${pastDeeds.size})`} />
            {me?.hasPrivilege(Privileges.DONATION_VIEW) && (
              <Tab value="donations" label={`Donations (${donations.size})`} />
            )}
            {me?.hasPrivilege(Privileges.DONATION_SCHEDULE_VIEW) && (
              <Tab
                value="donationSchedules"
                label={`Donations Schedules (${donationSchedules.size})`}
              />
            )}
            {me?.hasPrivilege(Privileges.VOLUNTEERTIME) && (
              <Tab value="volunteerHours" label={`Volunteer Hours (${volunteerTimes.size})`} />
            )}
            {me?.hasFeature(Features.VOLUNTEER_TIME_OFF) && (
              <Tab value="volunteerTimeOff" label={`VTO Requests (${volunteerTimesOff.size})`} />
            )}
            {me?.hasFeature(Features.DONATION_CREDIT) &&
              me?.hasPrivilege(Privileges.DONATION_VIEW) && (
                <Tab
                  value="donationCredit"
                  label={
                    <Tooltip title="This balance is an estimated sum of all historical credit. If from another currency, this amount is an estimate based on the current exchange rate, which will vary.">
                      <span>
                        Credit (
                        {currenciesFormat(
                          volunteer.donationCreditsAmountCurrencies,
                          me.displayCurrency
                        )}
                        )
                      </span>
                    </Tooltip>
                  }
                />
              )}
          </Tabs>
        </Box>
        {loading ? (
          <LinearProgress />
        ) : (
          <>
            {(tab === 'upcomingDeeds' || tab === 0) && (
              <>
                {upcomingDeeds.size > 0 &&
                  upcomingDeeds.toArray().map((deed) => <Deed key={deed.id} deed={deed} />)}
                {upcomingDeeds.size === 0 && (
                  <div style={{ marginTop: 30 }}>No upcoming deeds to show</div>
                )}
              </>
            )}
            {tab === 'completedDeeds' && (
              <>
                {pastDeeds.size > 0 &&
                  pastDeeds.toArray().map((deed) => <Deed key={deed.id} deed={deed} />)}
                {pastDeeds.size === 0 && (
                  <div style={{ marginTop: 30 }}>No completed deeds to show</div>
                )}
              </>
            )}
            {me?.hasPrivilege(Privileges.DONATION_VIEW) && tab === 'donations' && (
              <>
                {donations.size > 0 &&
                  donations
                    .toArray()
                    .map((donation) => (
                      <Donation key={donation.id} donation={donation} user={me} />
                    ))}
                {donations.size === 0 && <div style={{ marginTop: 30 }}>No donations to show</div>}
              </>
            )}
            {me?.hasPrivilege(Privileges.DONATION_SCHEDULE_VIEW) && tab === 'donationSchedules' && (
              <>
                {donationSchedules.size > 0 ? (
                  donationSchedules
                    .toArray()
                    .map((donationSchedule) => (
                      <DonationSchedule
                        key={donationSchedule.id}
                        donationSchedule={donationSchedule}
                        user={me}
                      />
                    ))
                ) : (
                  <div style={{ marginTop: 30 }}>No donations schedules to show</div>
                )}
              </>
            )}
            {me?.hasPrivilege(Privileges.VOLUNTEERTIME) && tab === 'volunteerHours' && (
              <>
                {volunteerTimes.size > 0 &&
                  volunteerTimes
                    .toArray()
                    .map((volunteerTime) => (
                      <VolunteerHoursAndTimeOff
                        key={volunteerTime.id}
                        volunteerTime={volunteerTime}
                      />
                    ))}
                {volunteerTimes.size === 0 && (
                  <div style={{ marginTop: 30 }}>No Volunteer Hours to show</div>
                )}
              </>
            )}
            {me?.hasFeature(Features.VOLUNTEER_TIME_OFF) && tab === 'volunteerTimeOff' && (
              <>
                {volunteerTimesOff.size > 0 &&
                  volunteerTimesOff
                    .toArray()
                    .map((volunteerTimeOff) => (
                      <VolunteerHoursAndTimeOff
                        key={volunteerTimeOff.id}
                        volunteerTime={volunteerTimeOff}
                      />
                    ))}
                {volunteerTimesOff.size === 0 && (
                  <div style={{ marginTop: 30 }}>No Volunteer Time Off to show</div>
                )}
              </>
            )}
            {me?.hasPrivilege(Privileges.DONATION_VIEW) && tab === Features.DONATION_CREDIT && (
              <>
                {walletTransactions.size > 0 &&
                  walletTransactions
                    .toArray()
                    .map((walletTransaction) => (
                      <WalletTransaction
                        key={walletTransaction.id}
                        walletTransaction={walletTransaction}
                      />
                    ))}
                {walletTransactions.size === 0 && (
                  <div style={{ marginTop: 30 }}>No Credits to show</div>
                )}
              </>
            )}
          </>
        )}
      </Box>

      {volunteerIsFromMyCompany &&
        me?.hasFeature(Features.EMPLOYEE_AFFILIATIONS) &&
        me?.hasPrivilege(Privileges.EMPLOYEE_AFFILIATIONS_VIEW) && <AffiliationsList />}

      {formResponses && formResponses.length > 0 && (
        <Box style={{ marginTop: 30 }}>
          <h2 style={{ fontSize: 25, margin: 0 }}>Form responses</h2>
          <br />
          {formResponses.map((formResponse) => {
            const { form } = formResponse;
            if (!form) {
              return null;
            }
            return (
              <Fragment key={form.id}>
                <Typography
                  type="headline"
                  component="h2"
                  style={{ fontSize: 17, fontWeight: 500 }}
                >
                  {form.name}
                </Typography>
                <br />
                {form.questions.map((question) => {
                  const questionResponse = formResponse.data.find(
                    (item) => String(item.question) === String(question._id)
                  );
                  if (!questionResponse) {
                    return null;
                  }
                  const questionResponseValue = questionResponse.value;
                  let renderedQuestionResponse = '';
                  if (Array.isArray(questionResponseValue)) {
                    renderedQuestionResponse = questionResponseValue.join(', ');
                  }
                  if (typeof questionResponseValue === 'string') {
                    renderedQuestionResponse = questionResponseValue;
                  }
                  return (
                    <Fragment key={question._id}>
                      {question.questionType !== 'Checkbox' ? (
                        <Typography
                          type="headline"
                          component="h3"
                          style={{ fontSize: 15, fontWeight: 500 }}
                        >
                          {question.title}
                        </Typography>
                      ) : null}
                      {renderedQuestionResponse ? (
                        <>
                          <Typography component="p" style={{ fontSize: 15, fontWeight: 300 }}>
                            {question.questionType === 'Checkbox' ? (
                              <CheckBoxIcon style={{ paddingRight: 5 }} />
                            ) : null}
                            {renderedQuestionResponse.split('\n').map((item, index) => (
                              <span key={item || index}>
                                {item}
                                <br />
                              </span>
                            ))}
                          </Typography>
                          <br />
                        </>
                      ) : null}
                    </Fragment>
                  );
                })}
              </Fragment>
            );
          })}
        </Box>
      )}

      <ImpersonationModal
        open={showImpersonationModal}
        onClose={() => setShowImpersonationModal(false)}
        onSubmit={({ impersonationReason }) => {
          dispatch(Actions.impersonateAction(volunteer.id, impersonationReason));
          setShowImpersonationModal(false);
        }}
        userName={formatFullName(volunteer.fullName)}
      />
    </>
  );
};

export default VolunteerPage;
